// Stepper.tsx  ← drop-in replacement
import { Box, useTheme } from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'

export interface Step {
  key: string
  icon?: string
  label?: string
}

interface StepperProps {
  steps: Step[]
  current: string
  barColor?: string
  nodeSize?: number
  thickness?: number
  gap?: number
  renderBelow?: (step: Step, reached: boolean, current: boolean) => React.ReactNode
}

const Stepper = ({
  steps,
  current,
  barColor = '#fbbf24',
  nodeSize = 28,
  thickness = 8,
  gap = 12,
  renderBelow,
}: StepperProps) => {
  const idx = steps.findIndex((s) => s.key === current)
  const theme = useTheme()

  return (
    <Box
      sx={{
        display: 'flex',
        columnGap: `${gap}px`,
        rowGap: 6,
        overflow: 'auto',
      }}
    >
      {steps.map((s, i) => {
        const reached = i <= idx
        const isCurrent = i === idx

        return (
          <Box
            key={s.key}
            sx={{
              width: 82,
              textAlign: 'center',
              position: 'relative',
              // connector
              '&:not(:last-of-type)::after': {
                content: '""',
                position: 'absolute',
                top: nodeSize / 2 - thickness / 2,
                left: `calc(50% + ${nodeSize / 2}px)`,
                width: `calc(100% + ${gap}px - ${nodeSize}px)`,
                height: thickness,
                bgcolor: barColor,
                zIndex: 0,
                borderBottom: `2px solid ${theme.palette.secondary.main}`,
                borderTop: `2px solid ${theme.palette.secondary.main}`,
              },
            }}
          >
            <Box
              sx={{
                width: nodeSize,
                height: nodeSize,
                mx: 'auto',
                borderRadius: '50%',
                border: `3px solid ${theme.palette.secondary.main}`,
                bgcolor: barColor, // always yellow
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                zIndex: 1,
              }}
            >
              {reached && <CheckIcon sx={{ fontSize: 20, color: 'light.main' }} />}
            </Box>

            {isCurrent && (
              <Box
                sx={{
                  position: 'absolute',
                  top: nodeSize + 8,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 0,
                  height: 0,
                  borderLeft: '8px solid transparent',
                  borderRight: '8px solid transparent',
                  borderBottom: `10px solid  ${theme.palette.secondary.main}`,
                }}
              />
            )}

            {renderBelow?.(s, reached, isCurrent)}
          </Box>
        )
      })}
    </Box>
  )
}

export default Stepper
